import {
    type DocumentNode,
    type GraphQLSchema,
    type SelectionNode,
    buildSchema,
    graphql,
    parse,
} from 'graphql';

import { TOOL_NAMES } from '../../toolNames';
import { SHORT_SCENE_ID_LENGTH, computeShortIds } from '../../tools/scene-addressing';
import type {
    AssetDescription,
    SceneDescription,
    SceneGroupDescription,
    TranscriptDescription,
    VideoStateSnapshot,
} from '../types';

/** The slice of a subagent scope the query layer needs: which scene groups it owns. */
type SceneGroupScopeLike = { getSceneGroupIds(): string[] };

import { VIDEO_STATE_SCHEMA_SDL } from './schema';

/**
 * The video state is four levels deep (Video → SceneGroup → Scene → Asset), so any query nesting
 * much deeper than that is a mistake rather than a real need. We reject it before executing instead
 * of letting it run, both to bound cost and to nudge the model back toward the flat shape.
 */
export const DEFAULT_MAX_QUERY_DEPTH = 8;

// Graphql-js calls a field's resolver with (args, context, info). Building the resolver root as a
// tree of plain values and *functions* (for the fields that take arguments) lets the default field
// resolver do all the wiring — no resolver map, no schema-tools dependency. A function field is
// invoked with its args; a value field is returned as-is.
type ResolverField = unknown;
type ResolverNode = Record<string, ResolverField>;

let cachedSchema: GraphQLSchema | undefined;
function getSchema(): GraphQLSchema {
    cachedSchema ??= buildSchema(VIDEO_STATE_SCHEMA_SDL);
    return cachedSchema;
}

function includes(haystack: string | undefined, needle: string): boolean {
    return (haystack ?? '').toLowerCase().includes(needle.toLowerCase());
}

/** A short handle matches when the query string is a case-insensitive prefix of it (or vice-versa). */
function handleMatches(shortId: string, fullId: string | undefined, query: string): boolean {
    const q = query.trim().toLowerCase();
    return shortId.toLowerCase().startsWith(q) || (fullId?.toLowerCase().startsWith(q) ?? false);
}

function buildAssetNode(asset: AssetDescription): ResolverNode {
    const described = asset as Record<string, unknown>;
    return {
        name: asset.name,
        type: asset.type,
        bounds: asset.bounds,
        subscene: described.subscene ?? null,
        appearsAt: described.appearsAt ?? null,
        text: described.text ?? null,
        mediaId: described.mediaId ?? null,
        mediaType: described.mediaType ?? null,
        description: described.description ?? null,
        // The whole description, passed through the JSON scalar — nothing the flat state shows is lost.
        properties: () => asset,
    };
}

function buildSceneNode(scene: SceneDescription, shortId: string): ResolverNode {
    const assets = scene.assets.map(buildAssetNode);
    return {
        id: shortId,
        globalIndex: scene.globalIndex,
        sceneGroupIndex: scene.sceneGroupIndex,
        design: scene.design,
        sceneCategory: scene.sceneCategory ?? null,
        duration: scene.duration,
        subscenes: scene.subscenes,
        covers: scene.coverage?.words ?? null,
        cues: scene.coverage?.cues ?? null,
        role: scene.coverage?.role ?? null,
        captionsVisible: scene.captionsVisible ?? null,
        transition: scene.transition ?? null,
        textPositionPresets: scene.textPositionPresets,
        assets: (args: { type?: string }) =>
            args.type == null
                ? assets
                : assets.filter(
                      (asset) =>
                          String(asset.type).toLowerCase() === args.type!.trim().toLowerCase(),
                  ),
    };
}

function buildTranscriptNode(transcript: TranscriptDescription): ResolverNode {
    const spokenText = transcript.words
        .filter((word) => !word.isPause)
        .map((word) => word.word)
        .join(' ');
    return {
        language: transcript.language,
        wordCount: transcript.words.length,
        text: spokenText,
        // Word times come from `seconds * 1000` unrounded, so they are floats (e.g. 2009.9999…).
        // graphql-js's Int serializer throws on a non-integer, which — through the non-null
        // `[Word!]!` — would null the whole transcript. Round to integer ms (the useful unit anyway).
        words: transcript.words.map((word) => ({
            word: word.word,
            startTime: Math.round(word.startTime),
            endTime: Math.round(word.endTime),
            isPause: word.isPause ?? null,
        })),
    };
}

function sceneContainsText(scene: SceneDescription, needle: string): boolean {
    if (includes(scene.coverage?.words, needle) || includes(scene.coverage?.cues, needle)) {
        return true;
    }
    return scene.assets.some((asset) =>
        includes((asset as Record<string, unknown>).text as string | undefined, needle),
    );
}

/** Precomputed short-id maps (scene id → handle, group id → handle), keyed by full luminary id. */
export type ShortIdMaps = { scenes: Map<string, string>; groups: Map<string, string> };

export function computeShortIdMaps(snapshot: VideoStateSnapshot): ShortIdMaps {
    return {
        scenes: computeShortIds(
            snapshot.scenes.map((scene) => scene.id).filter((id): id is string => id != null),
            SHORT_SCENE_ID_LENGTH,
        ),
        groups: computeShortIds(
            snapshot.sceneGroups.map((group) => group.id).filter((id): id is string => id != null),
            SHORT_SCENE_ID_LENGTH,
        ),
    };
}

/**
 * Builds the resolver tree. `shortIds` is passed in rather than computed here so a scoped query can
 * use handles computed over the *whole* video: computing them over the filtered subset would hand
 * out shorter handles than the flat state shows, and an editing tool would then reject them as
 * ambiguous. Defaults to computing over `snapshot` for an unscoped build.
 */
export function buildVideoStateRoot(
    snapshot: VideoStateSnapshot,
    shortIds: ShortIdMaps = computeShortIdMaps(snapshot),
): ResolverNode {
    const sceneShortIds = shortIds.scenes;
    const groupShortIds = shortIds.groups;
    const sceneShortId = (scene: SceneDescription) =>
        (scene.id && sceneShortIds.get(scene.id)) || `scene_${scene.globalIndex}`;
    const groupShortId = (group: SceneGroupDescription) =>
        (group.id && groupShortIds.get(group.id)) || `group_${group.index}`;

    const sceneNodesByGlobalIndex = new Map<number, ResolverNode>();
    for (const scene of snapshot.scenes) {
        sceneNodesByGlobalIndex.set(scene.globalIndex, buildSceneNode(scene, sceneShortId(scene)));
    }
    const scenesForGroup = (group: SceneGroupDescription) =>
        group.sceneIndices
            .map((globalIndex) => sceneNodesByGlobalIndex.get(globalIndex))
            .filter((node): node is ResolverNode => node != null);

    const groupNodes: ResolverNode[] = snapshot.sceneGroups.map((group) => {
        const scenes = scenesForGroup(group);
        const transcript = group.transcript ? buildTranscriptNode(group.transcript) : null;
        const transcriptText =
            group.transcript?.words
                .filter((word) => !word.isPause)
                .map((word) => word.word)
                .join(' ') ?? '';
        return {
            id: groupShortId(group),
            index: group.index,
            type: group.type,
            duration: group.duration,
            voice: group.voice ?? null,
            voiceSpeed: group.voiceSpeed ?? null,
            voiceVolume: group.voiceVolume ?? null,
            musicVolume: group.musicVolume == null ? null : String(group.musicVolume),
            fillerWords: group.fillerWords ?? null,
            transcript,
            __transcriptText: transcriptText,
            scenes: (args: { id?: string; containsText?: string }) => {
                let result = scenes;
                if (args.id != null) {
                    result = result.filter((node) =>
                        handleMatches(String(node.id), undefined, args.id!),
                    );
                }
                if (args.containsText != null) {
                    result = result.filter((node) => {
                        const scene = snapshot.scenes.find(
                            (candidate) => candidate.globalIndex === node.globalIndex,
                        );
                        return scene ? sceneContainsText(scene, args.containsText!) : false;
                    });
                }
                return result;
            },
        };
    });

    const video: ResolverNode = {
        resolution: snapshot.video.resolution,
        aspectRatio: snapshot.video.aspectRatio,
        sceneCount: snapshot.video.sceneCount,
        duration: snapshot.video.duration,
        colorGrading: snapshot.video.colorGrading ?? null,
        designSystem: snapshot.video.designSystem ?? null,
        template: {
            template: snapshot.template.template,
            fonts: snapshot.template.templateFonts,
            colors: snapshot.template.templateColors,
            palettes: snapshot.template.templatePalettes,
        },
        music: snapshot.music
            ? {
                  id: snapshot.music.id,
                  volume: snapshot.music.volume,
                  title: snapshot.music.title,
              }
            : null,
        captions: snapshot.captions
            ? {
                  enabled: snapshot.captions.enabled,
                  sceneDesignId: snapshot.captions.sceneDesignId ?? null,
                  bounds: snapshot.captions.bounds ?? null,
                  fontSize: snapshot.captions.fontSize ?? null,
                  textColor: snapshot.captions.textColor ?? null,
                  spokenWordHighlightColor: snapshot.captions.spokenWordHighlightColor ?? null,
                  font: snapshot.captions.font ?? null,
              }
            : null,
        sceneGroups: (args: { id?: string; type?: string; containsText?: string }) => {
            let result = groupNodes;
            if (args.id != null) {
                result = result.filter((node) =>
                    handleMatches(String(node.id), undefined, args.id!),
                );
            }
            if (args.type != null) {
                result = result.filter(
                    (node) => String(node.type).toLowerCase() === args.type!.trim().toLowerCase(),
                );
            }
            if (args.containsText != null) {
                result = result.filter((node) =>
                    includes(node.__transcriptText as string, args.containsText!),
                );
            }
            return result;
        },
    };

    const sceneNodeById = (id: string) => {
        for (const node of sceneNodesByGlobalIndex.values()) {
            if (handleMatches(String(node.id), undefined, id)) {
                return node;
            }
        }
        return null;
    };

    return {
        video,
        scene: (args: { id: string }) => sceneNodeById(args.id),
    };
}

/** The deepest selection-set nesting in the query, so an over-deep query can be rejected early. */
export function maxQueryDepth(document: DocumentNode): number {
    let max = 0;
    const walk = (selections: readonly SelectionNode[], depth: number) => {
        max = Math.max(max, depth);
        for (const selection of selections) {
            if ('selectionSet' in selection && selection.selectionSet) {
                walk(selection.selectionSet.selections, depth + 1);
            }
        }
    };
    for (const definition of document.definitions) {
        if (definition.kind === 'OperationDefinition') {
            walk(definition.selectionSet.selections, 1);
        }
    }
    return max;
}

/**
 * Narrow a snapshot to a parallel subagent's own scene groups, so a query can't read the scenes,
 * assets or narration of the groups another subagent owns. Video-level facts (template, music,
 * captions) are shared and left intact, matching what the flat scoped state shows.
 */
function filterSnapshotToScope(
    snapshot: VideoStateSnapshot,
    scope: SceneGroupScopeLike,
): VideoStateSnapshot {
    const allowedGroupIds = new Set(scope.getSceneGroupIds());
    const sceneGroups = snapshot.sceneGroups.filter(
        (group) => group.id != null && allowedGroupIds.has(group.id),
    );
    const allowedSceneIndices = new Set(sceneGroups.flatMap((group) => group.sceneIndices));
    const scenes = snapshot.scenes.filter((scene) => allowedSceneIndices.has(scene.globalIndex));
    return { ...snapshot, sceneGroups, scenes };
}

export type QueryVideoStateOptions = {
    maxDepth?: number;
    /** A parallel subagent's scope: the query sees only these scene groups' scenes and narration. */
    sceneGroupScope?: SceneGroupScopeLike;
};

function errorPayload(message: string): string {
    return JSON.stringify(
        {
            errors: [
                {
                    message: `${message} See the schema in the ${TOOL_NAMES.getVideoState} description.`,
                },
            ],
        },
        null,
        2,
    );
}

/**
 * Run a GraphQL query against a captured video state snapshot and return the JSON result (or a JSON
 * error payload). Pure over the snapshot — no Luminary, no Redux — so it is cheap to test and the
 * returned values are exactly the snapshot's own rendered strings.
 */
export async function queryVideoState(
    snapshot: VideoStateSnapshot,
    query: string,
    { maxDepth = DEFAULT_MAX_QUERY_DEPTH, sceneGroupScope }: QueryVideoStateOptions = {},
): Promise<string> {
    if (query.trim() === '') {
        return errorPayload(
            `${TOOL_NAMES.getVideoState} needs a GraphQL \`query\` naming the fields you want — it no longer returns the whole video.`,
        );
    }

    let document: DocumentNode;
    try {
        document = parse(query);
    } catch (error) {
        const message = error instanceof Error ? error.message : 'Unknown parse error';
        return errorPayload(`Query syntax error: ${message}.`);
    }

    const depth = maxQueryDepth(document);
    if (depth > maxDepth) {
        return errorPayload(
            `Query is ${depth} levels deep (limit ${maxDepth}). The video state is only a few levels deep (video → scene group → scene → asset); ask for a flatter slice.`,
        );
    }

    // Compute handles over the whole video, then narrow the nodes — so a scoped query's ids match
    // the ids the flat state and the editing tools use (see buildVideoStateRoot).
    const shortIds = computeShortIdMaps(snapshot);
    const scopedSnapshot = sceneGroupScope
        ? filterSnapshotToScope(snapshot, sceneGroupScope)
        : snapshot;
    const result = await graphql({
        schema: getSchema(),
        source: query,
        rootValue: buildVideoStateRoot(scopedSnapshot, shortIds),
    });
    return JSON.stringify(result, null, 2);
}
